import React, { type MouseEvent, useEffect, useState } from 'react';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router';

import { ImgixImage } from '@fd/ui/Imgix/ImgixImage';

import { ReactComponent as ForkIcon } from '../../../assets/images/app_logo_fork.svg';
import generateSrcSet from '../../../ui/utils/generateSrcSet';
import OrgAndBrandSelector from '../OrgAndBrandSelector/OrgAndBrandSelector';
import { BrandSelectorMenuContent } from './components/BrandSelectorMenuContent';
import { useBrandSelector } from './hooks/useBrandSelector';
import { useSyncCurrentAppWithUrl } from './hooks/useSyncCurrentAppWithUrl';

const StyledMenuContainer = styled('div')({
  ':focus': {
    outline: 'none',
  },
  maxWidth: 400,
  minWidth: 304,
});

const StyledImgContainer = styled('div')({
  width: 40,
  height: 40,
  overflow: 'hidden',
  alignContent: 'center',
  display: 'flex',
  alignItems: 'center',
});

const StyledIconButton = styled(IconButton)({
  overflow: 'hidden',
});

/** Legacy top-bar brand selector: logo trigger, anchored menu and the org switcher drawer. */
const BrandSelector = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const menuOpen = Boolean(anchorEl);

  useSyncCurrentAppWithUrl();

  const brandSelector = useBrandSelector({
    enabled: menuOpen,
    onClose: () => setAnchorEl(null),
    onSwitchOrg: () => {
      setDrawerOpen(true);
      setAnchorEl(null);
    },
  });
  const {
    currentApp,
    currentOrg,
    orgId,
    brands,
    filteredBrands,
    loading,
    selectedIndex,
    maxVisibleItems,
    handleSearch,
    handleChange,
    handleClose,
    handleKeyDown,
  } = brandSelector;

  useEffect(() => {
    if (location?.search.includes('orgSearch')) {
      const params = new URLSearchParams(location.search);
      const orgSearch = params.get('orgSearch');
      orgSearch && setDrawerOpen(true);
    }
  }, []);

  const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const logoImageUrl = currentApp?.LogoImageUrl;
  const isImgixUrl = logoImageUrl?.includes('imgix.net');

  return (
    <>
      <StyledIconButton
        onClick={handleButtonClick}
        color="inherit"
        aria-label="Open App menu"
        data-fd="brand-selector"
      >
        <StyledImgContainer>
          {logoImageUrl ? (
            isImgixUrl ? (
              <ImgixImage
                src={logoImageUrl}
                sizes="40px"
                imgixParams={{
                  fit: 'inside',
                  auto: 'format,compress',
                  fm: 'png',
                  bg: 'transparent',
                }}
                alt={currentApp.Name}
              />
            ) : (
              <img
                srcSet={generateSrcSet(logoImageUrl, {
                  width: 40,
                  png: true,
                })}
                src={logoImageUrl}
                alt={currentApp.Name}
              />
            )
          ) : (
            <ForkIcon />
          )}
        </StyledImgContainer>
      </StyledIconButton>
      <Menu
        id="fade-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        MenuListProps={{
          autoFocus: true,
          'aria-labelledby': 'fade-button',
          onKeyDown: handleKeyDown,
        }}
      >
        <StyledMenuContainer>
          <BrandSelectorMenuContent
            currentApp={currentApp}
            orgId={currentOrg?.orgId || orgId}
            brands={brands}
            filteredBrands={filteredBrands}
            loading={loading}
            selectedIndex={selectedIndex}
            maxVisibleItems={maxVisibleItems}
            onSearch={handleSearch}
            onSelect={handleChange}
          />
        </StyledMenuContainer>
      </Menu>

      <ClickAwayListener
        onClickAway={() => drawerOpen && setDrawerOpen(false)}
        touchEvent="onTouchStart"
        mouseEvent="onMouseDown"
      >
        <OrgAndBrandSelector toggleOrgDrawer={() => setDrawerOpen(false)} open={drawerOpen} />
      </ClickAwayListener>
    </>
  );
};

export default BrandSelector;
